import {
  Component,
  computed,
  HostListener,
  inject,
  input,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  IconComponent,
  SelectComponent,
  TooltipComponent,
  TypographyComponent,
  type SelectOption,
} from '@purolator/ui-kit-angular';
import { TranslatePipe, TranslationService } from '../../../../core/i18n';
import {
  SIGNATURE_REQUIREMENT_ZS50,
  SIGNATURE_REQUIREMENT_NONE,
  SIGNATURE_REQUIREMENT_RES,
  type SignatureRequirement,
} from '../../forms/signature-protection.form';

const ALL_SIGNATURE_REQUIREMENTS: SignatureRequirement[] = [
  SIGNATURE_REQUIREMENT_NONE,
  SIGNATURE_REQUIREMENT_RES,
  SIGNATURE_REQUIREMENT_ZS50,
];

const SIGNATURE_TOOLTIP_KEY = 'createShipment.serviceStep.signatureProtection.tooltip';

/**
 * Strips HTML tags for screen-reader plain text without regex backtracking (Sonar S5852).
 * Replaces each tag with a space so adjacent text separated only by tags stays readable.
 */
function stripHtmlTags(html: string): string {
  let result = '';
  let insideTag = false;
  for (const char of html) {
    if (char === '<') {
      insideTag = true;
      continue;
    }
    if (char === '>' && insideTag) {
      insideTag = false;
      result += ' ';
      continue;
    }
    if (!insideTag) {
      result += char;
    }
  }
  return result.replace(/\s+/g, ' ').trim();
}

/**
 * Service-step signature protection card (Figma node 18693:51588).
 * Options follow Product Master OSNR/RES/ZS50 relations for the selected courier product.
 */
@Component({
  selector: 'app-signature-protection-section',
  imports: [
    ReactiveFormsModule,
    TypographyComponent,
    SelectComponent,
    IconComponent,
    TooltipComponent,
    TranslatePipe,
  ],
  templateUrl: './signature-protection-section.html',
  styleUrl: './signature-protection-section.scss',
})
export class SignatureProtectionSection implements OnDestroy {
  private readonly translation = inject(TranslationService);
  private readonly signatureTooltip = viewChild.required<TooltipComponent>('signatureTooltip');
  private tooltipBubbleObserver: MutationObserver | null = null;
  private tooltipObserverSafetyTimer: number | null = null;

  /** Plain-text signature help for aria-describedby (HTML tags stripped for SR). */
  protected readonly signatureTooltipPlainText = computed(() => {
    this.translation.revision();
    return stripHtmlTags(this.translation.translate(SIGNATURE_TOOLTIP_KEY));
  });

  /** Reactive form owned by the Service step page. */
  readonly form = input.required<
    FormGroup<{
      requirement: FormControl<SignatureRequirement>;
    }>
  >();

  /**
   * Allowed signature codes from Product Master relations.
   * Null keeps the full Phase 1 list until the selected product is known.
   */
  readonly availableRequirements = input<SignatureRequirement[] | null>(null);

  /**
   * Signature codes bundled in the selected product (Inclusive Product Master relations).
   * Inclusive RES is labeled "Signature Required (Included)" for evening products.
   */
  readonly includedRequirements = input<SignatureRequirement[]>([]);

  /**
   * Destination residential flag from service-ability / ship-to.
   * Commercial destinations treat Signature Required as complimentary (ESN parity)
   * and label it "Signature Required (Included)" even when RES is Optional in Product Master.
   */
  readonly destinationResidential = input(true);

  /**
   * OM-786 messaging scenario from destination type and whether OSNR is offered.
   * Evening = OSNR hidden; commercial/residential otherwise.
   */
  protected readonly messagingScenario = computed<'commercial' | 'residential' | 'evening'>(() => {
    const allowed = this.availableRequirements();
    const osnrOffered = allowed == null || allowed.includes(SIGNATURE_REQUIREMENT_NONE);
    if (!osnrOffered) {
      return 'evening';
    }
    return this.destinationResidential() ? 'residential' : 'commercial';
  });

  /**
   * i18n key prefix for the active OM-786 heading and helper copy.
   */
  protected readonly messagingKeyPrefix = computed(
    () => `createShipment.serviceStep.signatureProtection.scenarios.${this.messagingScenario()}`,
  );

  /**
   * Builds localized select options for the allowed signature codes.
   */
  protected readonly requirementOptions = computed<SelectOption[]>(() => {
    this.translation.revision();
    const allowed = this.availableRequirements() ?? ALL_SIGNATURE_REQUIREMENTS;
    const included = this.includedRequirements();
    const complimentaryRes =
      included.includes(SIGNATURE_REQUIREMENT_RES) || !this.destinationResidential();
    const resLabelKey = complimentaryRes
      ? 'createShipment.serviceStep.signatureProtection.options.RESIncluded'
      : 'createShipment.serviceStep.signatureProtection.options.RES';
    const labels: Record<SignatureRequirement, string> = {
      [SIGNATURE_REQUIREMENT_NONE]: this.translation.translate(
        'createShipment.serviceStep.signatureProtection.options.none',
      ),
      [SIGNATURE_REQUIREMENT_RES]: this.translation.translate(resLabelKey),
      [SIGNATURE_REQUIREMENT_ZS50]: this.translation.translate(
        'createShipment.serviceStep.signatureProtection.options.ZS50',
      ),
    };

    return ALL_SIGNATURE_REQUIREMENTS.filter((value) => allowed.includes(value)).map((value) => ({
      value,
      label: labels[value],
    }));
  });

  ngOnDestroy(): void {
    this.disconnectTooltipBubbleObserver();
  }

  /**
   * Shows the signature tooltip on keyboard focus after arming HTML formatting.
   */
  protected showSignatureTooltip(): void {
    this.prepareSignatureTooltipHtml();
    this.signatureTooltip().show();
  }

  /**
   * Arms a one-shot observer so the next kit tooltip bubble is HTML-formatted
   * while it is still visibility:hidden — before the kit's rAF reveals it.
   * puro-tooltip only assigns textContent, so without this the HTML tags flash as plain text.
   */
  protected prepareSignatureTooltipHtml(): void {
    this.disconnectTooltipBubbleObserver();

    const html = this.translation.translate(SIGNATURE_TOOLTIP_KEY);
    this.tooltipBubbleObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (!(node instanceof HTMLElement) || node.getAttribute('role') !== 'tooltip') {
            continue;
          }
          node.innerHTML = html;
          this.disconnectTooltipBubbleObserver();
          return;
        }
      }
    });
    this.tooltipBubbleObserver.observe(document.body, { childList: true });

    this.tooltipObserverSafetyTimer = window.setTimeout(() => {
      this.disconnectTooltipBubbleObserver();
    }, 1000);
  }

  /**
   * Stops observing for tooltip bubbles and clears the safety timer.
   */
  private disconnectTooltipBubbleObserver(): void {
    this.tooltipBubbleObserver?.disconnect();
    this.tooltipBubbleObserver = null;
    if (this.tooltipObserverSafetyTimer !== null) {
      window.clearTimeout(this.tooltipObserverSafetyTimer);
      this.tooltipObserverSafetyTimer = null;
    }
  }

  /**
   * Keeps the kit select menu open through option mousedown so click can select.
   * puro-select options are non-focusable; without this, combobox blur closes the
   * menu before click (same pattern as Express packaging unit select).
   */
  @HostListener('mousedown', ['$event'])
  protected preventOptionBlur(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.puro-select__option')) {
      event.preventDefault();
    }
  }
}
